import React, { useState } from 'react';
import { SiYoutube, SiInstagram, SiLinkedin, SiTiktok, SiPinterest } from 'react-icons/si';
import { FaRocket, FaCheck, FaSpinner, FaTwitter, FaBlog, FaExternalLinkAlt } from 'react-icons/fa';

interface DistributionPanelProps {
  platforms: any[];
  isApproved: boolean;
  onDistribute: (platformId: string) => void;
  onDistributeAll: () => void;
}

interface PlatformState {
  isPosting: boolean;
  isPosted: boolean;
  viewCount: number;
  likes: number;
  shares: number;
}

const PLATFORM_CONFIG: Record<string,{gradient:string;iconColor:string;contentType:string}> = {
  youtube:   {gradient:'linear-gradient(135deg,#FF0000,#CC0000)',iconColor:'text-red-600',contentType:'Short Video (60s)'},
  instagram: {gradient:'linear-gradient(135deg,#E1306C,#833AB4)',iconColor:'text-pink-600',contentType:'Reel / Story'},
  linkedin:  {gradient:'linear-gradient(135deg,#0077B5,#005885)',iconColor:'text-blue-600',contentType:'Article Post'},
  tiktok:    {gradient:'linear-gradient(135deg,#010101,#69C9D0)',iconColor:'text-cyan-600',contentType:'Short Video'},
  pinterest: {gradient:'linear-gradient(135deg,#E60023,#ad081b)',iconColor:'text-red-700',contentType:'Infographic Pin'},
  twitter:   {gradient:'linear-gradient(135deg,#1DA1F2,#0d8ddc)',iconColor:'text-sky-500',contentType:'Thread Post'},
  blog:      {gradient:'linear-gradient(135deg,#6366f1,#4f46e5)',iconColor:'text-indigo-600',contentType:'Blog Article'},
};

const DistributionPanel: React.FC<DistributionPanelProps> = ({
  platforms, isApproved, onDistribute, onDistributeAll,
}) => {
  const socialHandles: Record<string,string> = (() => {
    try { return JSON.parse(localStorage.getItem('uxcf_social_handles') || '{}'); }
    catch { return {}; }
  })();

  const getBundle = () => {
    try {
      const raw = localStorage.getItem('uxcf_generated_bundles');
      const bs: any[] = raw ? JSON.parse(raw) : [];
      return bs[0] || null;
    } catch { return null; }
  };

  const getHandle = (name: string) => {
    const key = name.charAt(0).toUpperCase() + name.slice(1);
    return socialHandles[key] || socialHandles[name] || '';
  };

  const [platformStates, setPlatformStates] = useState<Record<string,PlatformState>>(
    platforms.reduce((acc, platform) => ({
      ...acc,
      [platform.platform]: { isPosting: false, isPosted: false, viewCount: 0, likes: 0, shares: 0 },
    }), {})
  );

  const [postedBundle, setPostedBundle] = useState<any>(null);

  const getPlatformIcon = (type: string) => {
    const cls = 'w-8 h-8';
    switch (type) {
      case 'youtube':   return <SiYoutube className={cls} />;
      case 'instagram': return <SiInstagram className={cls} />;
      case 'linkedin':  return <SiLinkedin className={cls} />;
      case 'tiktok':    return <SiTiktok className={cls} />;
      case 'pinterest': return <SiPinterest className={cls} />;
      case 'twitter':   return <FaTwitter className={cls} />;
      default:          return <FaBlog className={cls} />;
    }
  };

  const simulateEngagement = (platformId: string) => {
    let v=0,l=0,s=0;
    const mv=Math.floor(Math.random()*5000)+1000;
    const ml=Math.floor(mv*0.15);
    const ms=Math.floor(mv*0.05);
    const interval=setInterval(() => {
      v=Math.min(v+Math.floor(Math.random()*200)+50,mv);
      l=Math.min(l+Math.floor(Math.random()*30)+5,ml);
      s=Math.min(s+Math.floor(Math.random()*10)+1,ms);
      setPlatformStates(prev => ({...prev,[platformId]:{...prev[platformId],viewCount:v,likes:l,shares:s}}));
      if(v>=mv) clearInterval(interval);
    },100);
  };

  const handlePost = (platformId: string) => {
    if (!isApproved) return;
    const bundle = getBundle();
    setPlatformStates(prev => ({...prev,[platformId]:{...prev[platformId],isPosting:true}}));
    setTimeout(() => {
      setPlatformStates(prev => ({...prev,[platformId]:{...prev[platformId],isPosting:false,isPosted:true}}));
      if(bundle) setPostedBundle(bundle);
      simulateEngagement(platformId);
      onDistribute(platformId);
    }, 2000);
  };

  const handleDistributeAll = () => {
    if (!isApproved) return;
    platforms.forEach(p => { if (!platformStates[p.platform]?.isPosted) handlePost(p.platform); });
    onDistributeAll();
  };

  const fmt = (n: number) => n>=1000000?(n/1000000).toFixed(1)+'M':n>=1000?(n/1000).toFixed(1)+'K':String(n);

  const bundle = getBundle();
  const articleTitle = bundle?.blogPost?.title || bundle?.topic || 'Your Content';

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Distribution Panel</h2>
        <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">SIMULATED PUBLISHING</span>
      </div>

      {!isApproved && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 font-medium">Content must be approved before distribution</p>
        </div>
      )}

      {bundle && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-500 font-semibold uppercase tracking-wide mb-1">Content ready to publish</p>
          <p className="font-semibold text-blue-900 text-sm">{articleTitle}</p>
          {bundle.topic && <p className="text-xs text-blue-600 mt-1">Topic: {bundle.topic}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
        {platforms.map(platform => {
          const name = platform.platform as string;
          const cfg = PLATFORM_CONFIG[name] || PLATFORM_CONFIG['blog'];
          const state = platformStates[name] || {isPosting:false,isPosted:false,viewCount:0,likes:0,shares:0};
          const handle = getHandle(name);
          return (
            <div
              key={name}
              className={`relative rounded-xl p-6 shadow-md transition-all duration-300 ${state.isPosted ? 'ring-2 ring-green-400' : ''}`}
              style={{ background: cfg.gradient }}
            >
              <div className="flex items-center mb-4">
                <div className={cfg.iconColor + ' bg-white rounded-full p-3 shadow-md'}>
                  {getPlatformIcon(name)}
                </div>
                <div className="ml-4">
                  <h3 className="text-white font-bold text-lg capitalize">{name}</h3>
                  {handle && <p className="text-white/80 text-xs mt-0.5 font-mono">{handle}</p>}
                  <span className="inline-block mt-1 px-2 py-0.5 bg-white/30 rounded-full text-white text-xs">{cfg.contentType}</span>
                </div>
              </div>

              <div className="bg-white/20 rounded-lg p-3 mb-4 backdrop-blur-sm space-y-1">
                <div className="flex justify-between text-white text-sm"><span>Audience</span><span className="font-bold">{fmt(platform.audienceSize)}</span></div>
                <div className="flex justify-between text-white text-sm"><span>Engagement</span><span className="font-bold">{platform.engagementRate.toFixed(1)}%</span></div>
                <div className="flex justify-between text-white text-sm"><span>Best time</span><span className="font-bold">{platform.bestPostingTimes[0]?.substring(11,16) || 'TBD'} UTC</span></div>
              </div>

              {state.isPosted && (
                <div className="bg-white rounded-lg p-3 mb-4 space-y-1">
                  <p className="text-xs font-semibold text-green-600 mb-2">Published (simulated)</p>
                  {name === 'linkedin' && postedBundle && (
                    <p className="text-xs text-gray-600 italic mb-2">{postedBundle.blogPost?.excerpt || articleTitle}</p>
                  )}
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Views</span><span className="font-bold">{fmt(state.viewCount)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Likes</span><span className="font-bold">{fmt(state.likes)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-500">Shares</span><span className="font-bold">{fmt(state.shares)}</span></div>
                  {handle && (<div className="flex items-center gap-1 pt-1 border-t border-gray-100"><FaExternalLinkAlt className="w-3 h-3 text-blue-400" /><span className="text-xs text-blue-500 font-mono">{handle}</span></div>)}
                </div>
              )}

              <button
                onClick={() => handlePost(name)}
                disabled={!isApproved || state.isPosting || state.isPosted}
                className={'w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ' + (
                  state.isPosted  ? 'bg-green-500 text-white cursor-default' :
                  state.isPosting ? 'bg-gray-400 text-white cursor-wait' :
                  isApproved      ? 'bg-white text-gray-800 hover:bg-gray-100 shadow-md' :
                                    'bg-gray-300 text-gray-500 cursor-not-allowed'
                )}
              >
                {state.isPosting ? <><FaSpinner className="animate-spin" />Posting...</> :
                 state.isPosted  ? <><FaCheck />Posted!</> : 'Post Now'}
              </button>
            </div>
          );
        })}
      </div>

      <button
        onClick={handleDistributeAll}
        disabled={!isApproved || platforms.every(p => platformStates[p.platform]?.isPosted)}
        className={'w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all ' + (
          isApproved && !platforms.every(p => platformStates[p.platform]?.isPosted)
            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-lg'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        )}
      >
        <FaRocket />Launch All Platforms
      </button>

      {platforms.some(p => platformStates[p.platform]?.isPosted) && (
        <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
          <h3 className="font-bold text-gray-800 mb-3">Distribution Summary</h3>
          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
            <div><p className="text-2xl font-bold">{platforms.filter(p => platformStates[p.platform]?.isPosted).length}/{platforms.length}</p><p className="text-sm text-gray-600">Platforms</p></div>
            <div><p className="text-2xl font-bold">{fmt(platforms.reduce((s,p)=>s+(platformStates[p.platform]?.viewCount||0),0))}</p><p className="text-sm text-gray-600">Total Views</p></div>
            <div><p className="text-2xl font-bold">{fmt(platforms.reduce((s,p)=>s+(platformStates[p.platform]?.likes||0),0))}</p><p className="text-sm text-gray-600">Total Likes</p></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DistributionPanel;
